import firebase from 'firebase/app'
import {
  getFirebaseAuth,
  getFirebaseFirestore,
  requireFirebaseApiKey,
  requireFirebaseAuth,
} from './client'
import { getFirebaseAuthErrorCode } from './errors'
import { getHanaspeakApiBaseUrl } from './functions-api'
import { ensureLearnerTrialSafe } from './trial'

const singleDeviceSessionStorageKey = 'hanaspeak.singleDeviceSession.deviceId'

export interface RegisterAccountInput {
  readonly displayName: string
  readonly email: string
  readonly firstname: string
  readonly lastname: string
  readonly middlename: string
  readonly password: string
}

export interface RegisterAccountErrorDetails {
  readonly code: string | null
  readonly field: 'email' | 'password' | null
  readonly message: string
}

interface SessionClaimResponsePayload {
  readonly customToken?: unknown
  readonly error?: unknown
  readonly errorCode?: unknown
  readonly firebaseErrorCode?: unknown
  readonly sessionId?: unknown
}

interface PasswordSignInResponsePayload {
  readonly error?: {
    readonly message?: unknown
  }
  readonly idToken?: unknown
}

export interface ActiveSessionSnapshot {
  readonly activeSessionId: string | null
  readonly isFromCache: boolean
}

export type SingleDeviceLoginStage =
  | 'idle'
  | 'password-verifying'
  | 'claiming-session'
  | 'custom-token-signing-in'
  | 'active'
  | 'failed'

export interface SingleDeviceLoginOptions {
  readonly onStageChange?: (stage: SingleDeviceLoginStage) => void
}

function createBrowserDeviceId() {
  const cryptoApi = typeof window !== 'undefined'
    ? window.crypto as Crypto & { readonly randomUUID?: () => string }
    : null

  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID()
  }

  if (typeof cryptoApi?.getRandomValues === 'function') {
    const randomValues = new Uint32Array(4)
    cryptoApi.getRandomValues(randomValues)

    return Array.from(randomValues, (value) => value.toString(16).padStart(8, '0')).join('-')
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function getBrowserDeviceId() {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    const storedDeviceId = window.localStorage.getItem(singleDeviceSessionStorageKey)

    if (storedDeviceId) {
      return storedDeviceId
    }

    const nextDeviceId = createBrowserDeviceId()
    window.localStorage.setItem(singleDeviceSessionStorageKey, nextDeviceId)

    return nextDeviceId
  } catch {
    return undefined
  }
}

function getBrowserDeviceLabel() {
  if (typeof window === 'undefined') {
    return 'Web browser'
  }

  return [window.navigator.platform, window.navigator.language]
    .filter((value) => value.trim().length > 0)
    .join(' / ') || 'Web browser'
}

function getPasswordSignInErrorMessage(payload: PasswordSignInResponsePayload | null) {
  const code = typeof payload?.error?.message === 'string'
    ? payload.error.message
    : null

  switch (code) {
    case 'EMAIL_NOT_FOUND':
    case 'INVALID_LOGIN_CREDENTIALS':
    case 'INVALID_PASSWORD':
      return 'Invalid email or password.'
    case 'USER_DISABLED':
      return 'This account has been disabled.'
    default:
      return 'Unable to sign in right now. Please try again.'
  }
}

async function verifyPasswordWithoutClientSignIn(email: string, password: string) {
  const apiKey = requireFirebaseApiKey()
  const response = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${encodeURIComponent(apiKey)}`,
    {
      body: JSON.stringify({
        email,
        password,
        returnSecureToken: true,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    },
  )
  const payload = await response.json().catch(() => null) as PasswordSignInResponsePayload | null

  if (!response.ok) {
    throw new Error(getPasswordSignInErrorMessage(payload))
  }

  if (typeof payload?.idToken !== 'string' || payload.idToken.length === 0) {
    throw new Error('Firebase password verification did not return an ID token.')
  }

  return payload.idToken
}



function getSessionClaimErrorMessage(payload: SessionClaimResponsePayload | null) {
  const message = typeof payload?.error === 'string' && payload.error.trim().length > 0
    ? payload.error
    : 'Unable to start a secure login session. Please try again.'
  const errorCode = typeof payload?.errorCode === 'string' && payload.errorCode.trim().length > 0
    ? payload.errorCode
    : null
  const firebaseErrorCode = typeof payload?.firebaseErrorCode === 'string' && payload.firebaseErrorCode.trim().length > 0
    ? payload.firebaseErrorCode
    : null
  const codes = [errorCode, firebaseErrorCode].filter(Boolean).join(' / ')

  return codes ? `${message} (${codes})` : message
}

async function parseSessionClaimResponse(response: Response) {
  const payload = await response.json().catch(() => null) as SessionClaimResponsePayload | null

  if (!response.ok) {
    throw new Error(getSessionClaimErrorMessage(payload))
  }

  if (typeof payload?.customToken !== 'string' || payload.customToken.length === 0) {
    throw new Error('Session claim response did not include a custom token.')
  }

  if (typeof payload.sessionId !== 'string' || payload.sessionId.length === 0) {
    throw new Error('Session claim response did not include a session id.')
  }

  return {
    customToken: payload.customToken,
    sessionId: payload.sessionId,
  }
}

async function claimSingleDeviceSession(idToken: string) {
  const response = await fetch(`${getHanaspeakApiBaseUrl()}/auth/session/claim`, {
    body: JSON.stringify({
      deviceId: getBrowserDeviceId(),
      deviceLabel: getBrowserDeviceLabel(),
    }),
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  return parseSessionClaimResponse(response)
}

export const subscribeToAuthState = (
  onChange: (user: firebase.User | null) => void,
) => {
  const auth = getFirebaseAuth()

  if (!auth) {
    onChange(null)
    return () => undefined
  }

  return auth.onIdTokenChanged(
    (user) => {
      onChange(user)
    },
    () => {
      onChange(null)
    },
  )
}

export const loginWithEmailPassword = (email: string, password: string) =>
  requireFirebaseAuth().signInWithEmailAndPassword(email, password)

export const loginWithSingleDeviceSession = async (
  email: string,
  password: string,
  options: SingleDeviceLoginOptions = {},
) => {
  const auth = requireFirebaseAuth()

  try {
    options.onStageChange?.('password-verifying')
    const idToken = await verifyPasswordWithoutClientSignIn(email, password)

    options.onStageChange?.('claiming-session')
    const { customToken, sessionId } = await claimSingleDeviceSession(idToken)

    options.onStageChange?.('custom-token-signing-in')
    const sessionCredential = await auth.signInWithCustomToken(customToken)
    const sessionUser = sessionCredential.user ?? auth.currentUser
    const tokenResult = await sessionUser?.getIdTokenResult(true)
    const tokenSessionId = tokenResult?.claims.sid

    if (tokenSessionId !== sessionId) {
      throw new Error('Secure login session was created but the Firebase token did not include the expected session claim.')
    }

    options.onStageChange?.('active')

    return sessionCredential
  } catch (error) {
    options.onStageChange?.('failed')
    await auth.signOut().catch(() => undefined)
    throw error
  }
}

export const logoutCurrentUser = () => requireFirebaseAuth().signOut()

export const sendPasswordResetEmailToUser = (email: string) =>
  requireFirebaseAuth().sendPasswordResetEmail(email)

export const updateCurrentUserPassword = async (password: string) => {
  const auth = requireFirebaseAuth()

  if (!auth.currentUser) {
    throw new Error('Not authorized')
  }

  await auth.currentUser.updatePassword(password)
}

export class IncorrectCurrentPasswordError extends Error {
  constructor() {
    super('Current password is incorrect.')
    this.name = 'IncorrectCurrentPasswordError'
  }
}

export const changeCurrentUserPassword = async (
  currentPassword: string,
  newPassword: string,
) => {
  const auth = requireFirebaseAuth()
  const user = auth.currentUser

  if (!user) {
    throw new Error('Not authorized')
  }

  const email = user.email

  if (!email) {
    throw new Error('This account does not have an email address.')
  }

  // Verify the current password over the REST API first so the active
  // single-device session is never touched by a wrong-password attempt.
  try {
    await verifyPasswordWithoutClientSignIn(email, currentPassword)
  } catch (error) {
    if (error instanceof Error && error.message === 'Invalid email or password.') {
      throw new IncorrectCurrentPasswordError()
    }

    throw error
  }

  try {
    await user.updatePassword(newPassword)
  } catch (error) {
    if (getFirebaseAuthErrorCode(error) !== 'auth/requires-recent-login') {
      throw error
    }

    const credential = firebase.auth.EmailAuthProvider.credential(email, currentPassword)
    await user.reauthenticateWithCredential(credential)
    await user.updatePassword(newPassword)
  }
}

export const getUserActiveSessionId = async (
  user: firebase.User,
  forceRefresh = false,
) => {
  const tokenResult = await user.getIdTokenResult(forceRefresh)
  const sessionId = tokenResult.claims.sid

  return typeof sessionId === 'string' && sessionId.length > 0
    ? sessionId
    : null
}

export const subscribeToActiveSession = (
  userId: string,
  onChange: (snapshot: ActiveSessionSnapshot) => void,
  onError: (error: Error) => void,
) => {
  const firestore = getFirebaseFirestore()

  if (!firestore) {
    onChange({ activeSessionId: null, isFromCache: false })
    return () => undefined
  }

  return firestore.doc(`auth_sessions/${userId}`).onSnapshot(
    (snapshot) => {
      const data = snapshot.exists ? snapshot.data() : null
      const activeSessionId = data?.activeSessionId

      onChange({
        activeSessionId: typeof activeSessionId === 'string' && activeSessionId.length > 0
          ? activeSessionId
          : null,
        isFromCache: snapshot.metadata.fromCache,
      })
    },
    (error) => {
      onError(error)
    },
  )
}

export function normalizeRegisterAccountError(
  error: unknown,
): RegisterAccountErrorDetails {
  const code = getFirebaseAuthErrorCode(error)

  switch (code) {
    case 'EMAIL_EXISTS':
    case 'auth/email-already-in-use':
      return {
        code,
        field: 'email',
        message:
          'This email is already registered. Sign in or use Forgot your password? to recover access.',
      }
    case 'INVALID_EMAIL':
    case 'auth/invalid-email':
      return {
        code,
        field: 'email',
        message: 'Please enter a valid email address.',
      }
    case 'WEAK_PASSWORD':
    case 'auth/weak-password':
      return {
        code,
        field: 'password',
        message: 'Password must be at least 6 characters long.',
      }
    case 'auth/network-request-failed':
      return {
        code,
        field: null,
        message:
          'Network error while creating the account. Please check your connection and try again.',
      }
    default:
      if (error instanceof Error && error.message.trim().length > 0) {
        return {
          code,
          field: null,
          message: error.message,
        }
      }

      return {
        code,
        field: null,
        message: 'Unable to create the account right now. Please try again.',
      }
  }
}

export const registerAccount = async ({
  displayName,
  email,
  firstname,
  lastname,
  middlename,
  password,
}: RegisterAccountInput) => {
  const auth = requireFirebaseAuth()
  if (auth.currentUser?.email?.toLowerCase() === email.trim().toLowerCase()) {
    await auth.signInWithEmailAndPassword(email, password)
  } else {
    await auth.createUserWithEmailAndPassword(email, password)
  }

  if (!auth.currentUser) {
    throw new Error('Firebase registration completed without an active user.')
  }

  await auth.currentUser.updateProfile({
    displayName,
  })

  const profileResponse = await fetch(`${getHanaspeakApiBaseUrl()}/auth/password/profile/complete`, {
    body: JSON.stringify({ firstname, lastname, middlename }),
    headers: {
      Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
  if (!profileResponse.ok) {
    const payload = await profileResponse.json().catch(() => null) as { error?: unknown } | null
    throw new Error(typeof payload?.error === 'string' ? payload.error : 'Unable to complete account registration. Please try again.')
  }

  try {
    const { customToken, sessionId } = await claimSingleDeviceSession(await auth.currentUser.getIdToken())
    const sessionCredential = await auth.signInWithCustomToken(customToken)
    const sessionUser = sessionCredential.user ?? auth.currentUser
    const tokenResult = await sessionUser?.getIdTokenResult(true)

    if (tokenResult?.claims.sid !== sessionId) {
      throw new Error('Secure login session was created but the Firebase token did not include the expected session claim.')
    }
  } catch (error) {
    await auth.signOut().catch(() => undefined)
    throw error
  }

  await ensureLearnerTrialSafe()

  return auth.currentUser
}

export const waitForFirebaseAuthReady = () => {
  const auth = getFirebaseAuth()

  if (!auth) {
    return Promise.resolve<firebase.User | null>(null)
  }

  return new Promise<firebase.User | null>((resolve) => {
    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        unsubscribe()
        resolve(user)
      },
      () => {
        unsubscribe()
        resolve(null)
      },
    )
  })
}
